import React, { useState, useEffect } from "react";
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { formatUserIdentity } from "../../lib/formatting";
import { callBackendApiPost } from "../../lib/api";
import logDrtEvent from "../../lib/logging";
import ours from '@oursprivacy/cdp-sdk';

// You'll need to replace this with your actual Stripe publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const CreditCardForm = ({
  onSubmit,
  onNext,
  initialValue,
  user,
  paymentType = 'insurance',
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [message, setMessage] = useState("");

  // Check if Stripe is properly loaded
  useEffect(() => {
    if (stripe) {
      setStripeLoaded(true);
    } else {
      setStripeLoaded(false);
    }
  }, [stripe]);

  // Set initial value when editing
  useEffect(() => {
    if (initialValue) {
      // If editing, we might have stored card info, but for security we'll ask again
      // In a real app, you'd handle this differently
    }
  }, [initialValue]);

  const createPaymentIntent = async () => {
    const endpoint =
      paymentType === 'self-pay' ? '/stripe/pay-intent' : '/stripe/preauth-intent';

    const identity = user ? formatUserIdentity(user) : {};
    const body = {
      ...identity,
      amount: paymentType === 'insurance' ? 100 : (identity.amount ?? 11900),
      paymentType,
    };

    // If you have an auth token for the current user, pass it as the 3rd arg
    const res = await callBackendApiPost(endpoint, body);

    if (!res.ok) {
      logDrtEvent(`[conv] Failed to create PaymentIntent with status: ${res.status}`);
      const text = await res.text();
      throw new Error(`(${res.status}) ${text || 'Failed to create PaymentIntent'}`);
    }
    return res.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setCardError('Stripe is not loaded. Please refresh the page and try again.');
      return;
    }

    setIsProcessing(true);
    setCardError(null);
    setMessage("");

    const cardElement = elements.getElement(CardElement);

    try {
      // 1) Create the intent on your backend (includes amount + user identity)
      const intentPayload = await createPaymentIntent();
      const clientSecret = intentPayload.client_secret || intentPayload.clientSecret;
      if (!clientSecret) throw new Error('Missing client secret from server.');

      // 2) Confirm on client
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });
      if (error) {
        setCardError(`Payment error: ${error.message}`);
        setIsProcessing(false);
        return;
      }

      // 3) Outcomes
      const status = paymentIntent?.status;
      const ok =
        status === 'succeeded' ||
        (paymentType === 'insurance' && status === 'requires_capture');

      if (!ok) {
        setMessage('Your payment could not be completed. Please try again.');
        setIsProcessing(false);
        return;
      }

      const charge = paymentIntent?.charges?.data?.[0];
      const cardDetails = charge?.payment_method_details?.card;

      onSubmit?.({
        paymentIntentId: paymentIntent.id,
        amount: paymentIntent.amount,
        currency: paymentIntent.currency,
        status: paymentIntent.status,
        brand: cardDetails?.brand,
        last4: cardDetails?.last4,
        exp_month: cardDetails?.exp_month,
        exp_year: cardDetails?.exp_year,
        mode: paymentType,
      });

      // Track Purchase event with ours
      if (user?.email && user?.phoneNo) {
        ours.track('Purchase', {
          value: 119,
          currency: 'USD'
        }, {
          email: user.email,
          phone_number: user.phoneNo,
          custom_properties: {
            source: 'conversational intake'
          }
        });
      }

      onNext?.();

      cardElement.clear();
      setCardComplete(false);
      setIsProcessing(false);

      setMessage(paymentType === 'self-pay' ? 'Payment succeeded!' : 'Card authorized successfully!');
    } catch (err) {
      setCardError(err.message || 'An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
  };

  const canSubmit = stripe && stripeLoaded && cardComplete && !isProcessing;

  const isSelfPay = paymentType === 'self-pay';
  
  const instruction = isSelfPay
      ? 'Confirm your payment method'
      : 'Please enter your credit card information to place a $1 authorization.';

  const subtitle = isSelfPay
      ? 'For $119, your first visit with Piction Health includes a virtual consultation, a custom treatment plan made just for you, and continued care team support.'
      : "We usually charge your co-pay at the same time as your case is reviewed. We'll notify you if there are any additional costs related to your insurance.";

  const ctaLabel = isProcessing
    ? (isSelfPay ? 'Processing...' : 'Authorizing...')
    : (isSelfPay ? (user?.returning ? 'Pay $79.00' : 'Pay $119.00') : 'Authorize $1');

  return (
    <form onSubmit={handleSubmit} className="input-form">
      <div className="credit-card-container">
        <div className="credit-card-instructions">
          <p><strong>{instruction}</strong></p>
          <p style={{ color: '#666', fontSize: '0.8em', marginTop: '0.5em' }}>
            {subtitle}
          </p>
          {!stripeLoaded && (
            <p style={{ color: '#666', fontSize: '0.9em' }}>
              Loading payment system...
            </p>
          )}
        </div>
        <div className="credit-card-form">
          <div className="card-element-container">
            <label htmlFor="card-element">Card Information</label>
            <CardElement
              id="card-element"
              options={cardElementOptions}
              onChange={handleCardChange}
            />
            {(cardError || message) && (
              <div className="card-error" style={{ marginTop: 8 }}>
                {cardError || message}
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="credit-card-submit-button"
          disabled={!canSubmit}
        >
          {ctaLabel}
        </button>
      </div>
    </form>
  );
};

const CreditCardQuestion = ({
  onSubmit,
  onNext,
  initialValue,
  user,
  paymentType = 'insurance',
}) => {
  return (
    <Elements stripe={stripePromise}>
      <CreditCardForm
        onSubmit={onSubmit}
        onNext={onNext}
        initialValue={initialValue}
        user={user}
        paymentType={paymentType}
      />
    </Elements>
  );
};

export default CreditCardQuestion;
